/**
 * Outbound target-origin resolution for iframe → host postMessage traffic (CWE-668, #2113).
 *
 * The webapp runs inside an iframe embedded on many host domains, so — unlike the host → iframe
 * direction hardened in #2111 — there is no statically known parent origin to target: the embedder's
 * origin varies per customer (the same asymmetry #2092 is built on). We resolve the outbound target
 * from two sources, both of which yield the true embedder origin:
 *
 *   1. The `hostOrigin` query param the embed-script stamps onto the iframe URL from the host page's
 *      own `window.location.origin` (embed-script/iframeWindow.ts). This is the ANCHOR: it's read
 *      from the real parent at iframe-creation time, before any message exists, so it can't be
 *      influenced by inbound-message ordering. Captured once at module load — before React mounts —
 *      so a trusted target is known from first render, with no '*' window at all.
 *   2. As a FALLBACK, the origin of the first inbound host message that already passed the CWE-346
 *      source check (`event.source === window.parent`, see embed-script/messageOrigin.ts). This
 *      covers the standalone / no-embed-script case where no seed is stamped.
 *
 * The seed wins over a learned origin (#2276): a learned origin arrives on a message and could be
 * displaced by send ordering, whereas the seed cannot, so the seed is the stronger of the two.
 *
 * The URL seed exists because relying on (2) alone can deadlock: `featureflagsreceived` is skipped
 * while the origin is unknown, but the FAB that would drive the first origin-teaching message is
 * itself gated on those flags — so on host pages with no early SPA navigation the launcher never
 * appeared (review #2276). A redirect login replaces the whole document and returns to a bare
 * redirect_uri with no `hostOrigin`, so — like hostContext — the seed is PERSISTED (see below) to
 * survive that hop; and for any path that still ends up seedless, deferred posts are retried once an
 * origin is learned (see onParentOriginLearned).
 *
 * Only when neither source is available do we fall back to '*'. This is security-neutral:
 * postMessage's targetOrigin is a delivery FILTER, not a capability grant — the browser delivers to
 * window.parent only if the parent's current origin matches, so a stale or forged value can only make
 * it DROP the message, never deliver it to a non-parent origin. That fail-closed behaviour is exactly
 * what closes the CWE-668 window against a navigated-away or hijacked parent.
 */

/**
 * Validate a candidate origin. Rejects empty and opaque ('null') values, and anything that isn't a
 * well-formed serialized origin (`scheme://host[:port]`, no path). This matters for fail-closed
 * behaviour: `window.parent.postMessage(msg, 'foo')` throws a SyntaxError rather than dropping, so an
 * unvalidated seed (e.g. an attacker-forged `?hostOrigin=foo`) would turn every outbound post into a
 * thrown exception. Rejecting it here lets `getParentTargetOrigin` fall back to '*' instead — where
 * the data-bearing `featureflagsreceived` post is skipped and only best-effort control signals go out.
 * Browser-set values (event.origin, window.location.origin) always pass; only a hand-crafted URL fails.
 */
const usableOrigin = (origin: string | null | undefined): string | null => {
    if (!origin || origin === 'null') {
        return null;
    }
    try {
        // URL(x).origin round-trips to the same string only for a bare, valid origin; a path,
        // relative value, or garbage differs (or throws), so it is rejected.
        return new URL(origin).origin === origin ? origin : null;
    } catch {
        return null;
    }
};

// sessionStorage key for the persisted hostOrigin seed. Mirrors how hostContext is persisted for
// embedded mode (hostContextStorage's em_ key): the seed arrives once on the iframe URL, but a
// full-document redirect login (signinSilent → signinRedirect in useAuthSetup) returns to a bare
// redirect_uri with no `hostOrigin`. Without persistence the seed — unlike hostContext, which is
// re-hydrated from storage — would be lost and the launcher deadlock (#2276) would return on that
// path. sessionStorage survives same-tab navigations (the redirect round-trip is one) and is scoped
// per tab, which is the right lifetime for a single embedded session.
const HOST_ORIGIN_STORAGE_KEY = 'em_hostOrigin';

const sessionStore = (): Storage | null => {
    try {
        return window.sessionStorage;
    } catch {
        // sessionStorage access itself throws in some sandboxed / opaque-origin frames; degrade to
        // URL-only rather than crashing at module load.
        return null;
    }
};

/**
 * Resolve the seed once, at module load: the `hostOrigin` URL param if this document was rendered
 * with one, else the persisted value from an earlier render (redirect-login return). When the param
 * is present it is persisted and then STRIPPED from the URL — leaving it in `location.search` leaks
 * the embedder origin into every Snowplow page view (which keys on `location.search`) and into any
 * URL a user copies. Stripping is safe because the value is captured here before anything reads it
 * again. (#2276)
 */
const resolveHostOriginSeed = (): string | null => {
    const store = sessionStore();
    try {
        const fromUrl = usableOrigin(new URLSearchParams(window.location.search).get('hostOrigin'));
        if (fromUrl) {
            try {
                store?.setItem(HOST_ORIGIN_STORAGE_KEY, fromUrl);
            } catch {
                // Persistence is best-effort; the seed still works for this document load.
            }
            try {
                const url = new URL(window.location.href);
                url.searchParams.delete('hostOrigin');
                window.history.replaceState({}, '', url.toString());
            } catch {
                // Non-fatal: an environment without history/URL simply keeps the param in the bar.
            }
            return fromUrl;
        }
        return usableOrigin(store?.getItem(HOST_ORIGIN_STORAGE_KEY) ?? null);
    } catch {
        // Defensive: any environment without a parseable location leaves us on the '*' fallback.
        return null;
    }
};

let hostOriginSeed: string | null = resolveHostOriginSeed();

let trustedParentOrigin: string | null = null;

// One-shot callbacks to run when a usable outbound target first becomes known while it is currently
// '*'. Used to retry data-bearing posts (featureflagsreceived) that were deferred rather than dropped
// because no trusted target existed yet — see onParentOriginLearned / useInitialServiceInfo (#2276).
const originLearnedListeners = new Set<() => void>();

const flushOriginLearned = (): void => {
    // Snapshot and clear first: listeners are one-shot, and a listener that re-posts must not see
    // itself in a re-entrant flush.
    const listeners = [...originLearnedListeners];
    originLearnedListeners.clear();
    for (const listener of listeners) {
        try {
            listener();
        } catch {
            // A single deferred post throwing must not stop the others from flushing.
        }
    }
};

/**
 * Register a one-shot callback to run when a usable outbound target first becomes known (transition
 * away from '*'). Intended for posts that were deferred because the target was still '*'. Returns an
 * unsubscribe function. If a target is already known, callers should post directly instead of
 * registering — this only fires on the '*' → known transition. (#2276)
 */
export const onParentOriginLearned = (listener: () => void): (() => void) => {
    originLearnedListeners.add(listener);
    return () => originLearnedListeners.delete(listener);
};

/**
 * Record the origin of a host message that has ALREADY passed the inbound source check. Callers must
 * verify `event.source === window.parent` first — this only stores the origin, it does not vouch for
 * it. Empty and opaque ('null') origins are ignored: they can't serve as a postMessage target, and
 * storing one would mask a good origin learned earlier.
 */
export const rememberParentOrigin = (origin: string): void => {
    // Pin the FIRST valid origin only, never overwrite it. event.source === window.parent stays true
    // across a parent navigation (stable WindowProxy) while event.origin reflects the sender at send
    // time, so re-learning on a later message would let a navigated-away or hijacked parent retarget
    // our pending outbound posts — the exact CWE-668 window this is meant to close (#2113).
    if (trustedParentOrigin !== null || !usableOrigin(origin)) {
        return;
    }

    // A learned origin that disagrees with a present seed can't retarget us (the seed is the anchor in
    // getParentTargetOrigin), but surface it: it means the live parent origin and the seed disagree,
    // so browser-side targetOrigin matching would silently drop posts aimed at the seed. A visible
    // breadcrumb beats a dead close button with no error (#2276).
    if (hostOriginSeed && origin !== hostOriginSeed) {
        console.warn(
            `parentOrigin: learned host origin ${origin} conflicts with the embed-script seed ${hostOriginSeed}; keeping the seed as the outbound target.`,
        );
    }

    const hadUsableTarget = getParentTargetOrigin() !== '*';
    trustedParentOrigin = origin;
    // If this is the first usable outbound target we've had (i.e. there was no seed), flush any posts
    // deferred while the target was '*'. An origin learned from ANY source-checked inbound message —
    // not only a context update, which is the sole thing that re-runs useInitialServiceInfo — must be
    // able to unblock them, otherwise a host whose first message isn't a context update strands the
    // launcher for the whole session (#2276).
    if (!hadUsableTarget) {
        flushOriginLearned();
    }
};

/**
 * Target origin for iframe → host posts: the embed-script's `hostOrigin` seed (read from the real
 * parent at iframe creation, so it can't be reordered by inbound messages) first, then a learned
 * host-message origin as the standalone / no-embed-script fallback, else '*' until one is known.
 */
export const getParentTargetOrigin = (): string => hostOriginSeed ?? trustedParentOrigin ?? '*';

/**
 * Test-only: reset the module-level pins so a test can state its own preconditions instead of
 * depending on execution order (the seed and learned origin otherwise persist for the lifetime of the
 * bundle and leak between tests). Not part of the runtime API. (#2276)
 */
export const __resetParentOriginForTests = (): void => {
    hostOriginSeed = null;
    trustedParentOrigin = null;
    originLearnedListeners.clear();
};
